// The job's rollback evaluation profiles: per env_region, the baseline and
// rules the auto-rollback watch holds each deploy to. Not the version a
// rollback deploys.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Shield as ProfileIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useDeploymentData } from '../hooks/useDeploymentData';
import {
  baselineSignalRows,
  envRegionLabel,
  formatNumber,
  formatRule,
  isRolledBack,
  latestVersion,
  profileVersionKey,
  rollbackProfilesKey,
  scopeFields,
  sortProfileEntries,
} from '../lib/rollbackProfiles';
import { formatTimestamp } from '../lib/format';
import { getColors, sticker } from '../theme/tokens';
import { tagStyle } from '../theme/statusTokens';
import { RollbackProfileEntry } from '../../types/DeploymentRecord';
import EmptyState from './EmptyState';
import Help from './Help';
import {
  POINTER_REASON_HELP,
  PROFILE_FIELD_HELP as HELP,
  SIGNAL_HELP,
  SOURCE_HELP,
  TIER_HELP,
} from '../copy/rollbackProfileHelp';

const mono = {
  fontFamily: 'ui-monospace, Menlo, monospace',
  fontSize: '0.8rem',
};

export interface RollbackProfilesSectionProps {
  app: string;
  jobId: string;
}

const RollbackProfilesSection: React.FC<RollbackProfilesSectionProps> = ({ app, jobId }) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  const { wsStatus, rollbackProfiles, fetchRollbackProfiles, fetchRollbackProfileVersion } =
    useDeploymentData();

  useEffect(() => {
    if (wsStatus === 'connected' && app && jobId) fetchRollbackProfiles(app, jobId);
  }, [wsStatus, app, jobId, fetchRollbackProfiles]);

  const slice = rollbackProfiles.get(rollbackProfilesKey(app, jobId));
  const entries = useMemo(() => sortProfileEntries(slice?.entries || []), [slice?.entries]);

  const [selectedEnv, setSelectedEnv] = useState('');
  // Per env_region; absent means the current version.
  const [selectedVersions, setSelectedVersions] = useState<Record<string, number>>({});

  const current = entries.find(e => e.env_region === selectedEnv) || entries[0];
  const version = current ? (selectedVersions[current.env_region] ?? current.pointer.version) : 0;
  const versionState = current ? slice?.versions[profileVersionKey(current.env_region, version)] : undefined;
  const shown: RollbackProfileEntry | undefined =
    current && version === current.pointer.version ? current : versionState?.entry;

  // Fetching from state rather than the picker also resends a request a
  // dropped connection lost, since the hook forgets in-flight versions then.
  const currentEnv = current?.env_region;
  const isCurrent = !current || version === current.pointer.version;
  useEffect(() => {
    if (wsStatus === 'connected' && currentEnv && !isCurrent && !versionState) {
      fetchRollbackProfileVersion(app, jobId, currentEnv, version);
    }
  }, [wsStatus, app, jobId, currentEnv, isCurrent, version, versionState, fetchRollbackProfileVersion]);

  const pickVersion = (v: number) => {
    if (!current) return;
    setSelectedVersions(prev => ({ ...prev, [current.env_region]: v }));
  };

  let body: React.ReactNode;
  if (!slice || (slice.loading && !slice.loaded)) {
    body = (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: c.inkMuted,
        }}
      >
        <CircularProgress size={14} />
        <Typography variant="body2">Loading evaluation profiles…</Typography>
      </Box>
    );
  } else if (slice.error) {
    body = <Alert severity="error">{slice.error}</Alert>;
  } else if (!current) {
    body = (
      <EmptyState
        icon={<ProfileIcon />}
        title="No evaluation profile yet"
        body="One is bootstrapped after the first watched deploy."
      />
    );
  } else {
    body = (
      <>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2 }}>
          {entries.map(e => (
            <Chip
              key={e.env_region}
              label={envRegionLabel(e.env_region)}
              size="small"
              clickable
              color={e.env_region === current.env_region ? 'primary' : 'default'}
              variant={e.env_region === current.env_region ? 'filled' : 'outlined'}
              onClick={() => setSelectedEnv(e.env_region)}
            />
          ))}
        </Box>
        <VersionHeader entry={current} version={version} onPick={pickVersion} />
        {versionState?.loading && !shown && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: c.inkMuted,
              mt: 2,
            }}
          >
            <CircularProgress size={14} />
            <Typography variant="body2">Loading v{version}…</Typography>
          </Box>
        )}
        {versionState?.error && !shown && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {versionState.error}
          </Alert>
        )}
        {shown && <ProfileDetail entry={shown} />}
      </>
    );
  }

  // The job page's rollback tabs supply the frame and title.
  return (
    <Box>
      <Typography variant="body2" sx={{ color: c.inkMuted, mb: 2 }}>
        What the watch compares every deploy against, per env/region: the normal values for each signal (the
        baseline) and the rules that decide when a signal has breached. It changes only when the profile is
        updated, not with each deploy. This is not the version a rollback deploys.
      </Typography>
      {body}
    </Box>
  );
};

const VersionHeader: React.FC<{
  entry: RollbackProfileEntry;
  version: number;
  onPick: (v: number) => void;
}> = ({ entry, version, onPick }) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  const { pointer } = entry;
  const rolled = isRolledBack(entry);
  const rolledStyle = tagStyle(sticker.orange, theme.palette.mode);
  const options = [...entry.versions].sort((a, b) => b - a);
  if (!options.includes(pointer.version)) options.unshift(pointer.version);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
      <Tooltip title={HELP.version} arrow placement="top" enterDelay={200}>
        <Select
          size="small"
          value={version}
          onChange={e => onPick(Number(e.target.value))}
          sx={{ fontSize: '0.8rem', minWidth: 150 }}
          inputProps={{ 'aria-label': 'Profile version' }}
        >
          {options.map(v => (
            <MenuItem key={v} value={v} sx={{ fontSize: '0.8rem' }}>
              v{v}
              {v === pointer.version ? ' (current)' : ''}
            </MenuItem>
          ))}
        </Select>
      </Tooltip>
      <Typography variant="body2" sx={{ color: c.inkSecondary }}>
        <Help title={HELP.current}>Current v{pointer.version}</Help>
        {pointer.reason ? ' · ' : ''}
        {pointer.reason && <Help title={POINTER_REASON_HELP[pointer.reason]}>{pointer.reason}</Help>}
        {pointer.updated_at ? ` · ${formatTimestamp(pointer.updated_at)}` : ''}
      </Typography>
      {rolled && (
        <Tooltip
          title={`The profile pointer was moved back: v${latestVersion(entry)} is the newest version written, v${pointer.version} is live.`}
          arrow
        >
          <Chip
            label="Profile rolled back"
            size="small"
            sx={{
              backgroundColor: rolledStyle.bg,
              color: rolledStyle.text,
              border: `1px solid ${rolledStyle.border}`,
              fontWeight: 600,
              fontSize: '0.72rem',
              height: 22,
            }}
          />
        </Tooltip>
      )}
    </Box>
  );
};

const SectionLabel: React.FC<{ help?: string; children: React.ReactNode }> = ({ help, children }) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  return (
    <Typography variant="overline" sx={{ color: c.inkMuted, display: 'block', mt: 2.5, mb: 0.5 }}>
      <Help title={help}>{children}</Help>
    </Typography>
  );
};

const Field: React.FC<{
  label: string;
  help?: string;
  children: React.ReactNode;
}> = ({ label, help, children }) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  return (
    <Box>
      <Typography variant="caption" sx={{ color: c.inkMuted, display: 'block' }}>
        <Help title={help}>{label}</Help>
      </Typography>
      <Typography variant="body2" component="div">
        {children}
      </Typography>
    </Box>
  );
};

const ProfileDetail: React.FC<{ entry: RollbackProfileEntry }> = ({ entry }) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  const { baseline, provenance } = entry.profile;
  // Relayed unvalidated, so a stored null must not reach rules.length.
  const rules = entry.profile.rules ?? [];
  const signals = baselineSignalRows(baseline);
  const scope = scopeFields(baseline?.scope);
  const excluded = baseline?.excluded || [];
  const grid = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
    gap: 1.5,
  };
  const tableSx = {
    '& th': {
      color: c.inkMuted,
      fontWeight: 600,
      backgroundColor: c.canvasSoft,
    },
  };

  return (
    <Box>
      <SectionLabel help={HELP.baseline}>Baseline</SectionLabel>
      <Box sx={grid}>
        <Field label="Runs" help={HELP.runs}>
          {formatNumber(baseline?.n_runs)}
        </Field>
        <Field label="Low traffic" help={HELP.lowTraffic}>
          {baseline?.low_traffic ? 'Yes' : 'No'}
        </Field>
        <Field label="Built" help={HELP.built}>
          {formatTimestamp(baseline?.built_at) || '—'}
        </Field>
        {scope.map(s => (
          <Field key={s.label} label={s.label} help={HELP.scope}>
            <Box component="span" sx={mono}>
              {s.value}
            </Box>
          </Field>
        ))}
      </Box>
      {signals.length > 0 ? (
        <Table size="small" sx={{ mt: 1.5, ...tableSx }}>
          <TableHead>
            <TableRow>
              <TableCell>
                <Help title={HELP.signals}>Signal</Help>
              </TableCell>
              {['p50', 'p95', 'p99'].map(p => (
                <TableCell key={p} align="right">
                  <Help title={HELP.percentile}>{p}</Help>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {signals.map(s => (
              <TableRow key={s.signal}>
                <TableCell sx={mono}>
                  <Help title={SIGNAL_HELP[s.signal]}>{s.signal}</Help>
                </TableCell>
                <TableCell align="right">{formatNumber(s.p50)}</TableCell>
                <TableCell align="right">{formatNumber(s.p95)}</TableCell>
                <TableCell align="right">{formatNumber(s.p99)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography variant="body2" sx={{ color: c.inkMuted, mt: 1 }}>
          No baseline signals.
        </Typography>
      )}
      {excluded.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" sx={{ color: c.inkMuted, display: 'block' }}>
            <Help title={HELP.excluded}>Excluded deploys</Help>
          </Typography>
          {excluded.map(x => (
            <Typography key={x.deployment_id} variant="body2" sx={{ mb: 0.25 }}>
              <Box component="span" sx={mono}>
                {x.deployment_id}
              </Box>
              {x.reason ? ` — ${x.reason}` : ''}
            </Typography>
          ))}
        </Box>
      )}

      <SectionLabel help={HELP.rules}>Rules</SectionLabel>
      {rules.length > 0 ? (
        <Table size="small" sx={tableSx}>
          <TableHead>
            <TableRow>
              <TableCell>Signal</TableCell>
              <TableCell>
                <Help title={HELP.breachesWhen}>Breaches when</Help>
              </TableCell>
              <TableCell align="right">
                <Help title={HELP.sustain}>Sustain (min)</Help>
              </TableCell>
              <TableCell align="right">
                <Help title={HELP.minSample}>Min sample</Help>
              </TableCell>
              <TableCell align="right">
                <Help title={HELP.warmup}>Warmup (s)</Help>
              </TableCell>
              <TableCell>
                <Help title={HELP.tier}>Tier</Help>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rules.map(r => (
              <TableRow key={r.signal}>
                <TableCell sx={mono}>
                  <Help title={SIGNAL_HELP[r.signal]}>{r.signal}</Help>
                </TableCell>
                <TableCell>{formatRule(r.rule)}</TableCell>
                <TableCell align="right">{formatNumber(r.sustain_minutes)}</TableCell>
                <TableCell align="right">{formatNumber(r.min_sample)}</TableCell>
                <TableCell align="right">{formatNumber(r.warmup_seconds)}</TableCell>
                <TableCell>{r.tier ? <Help title={TIER_HELP[r.tier]}>{r.tier}</Help> : '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography variant="body2" sx={{ color: c.inkMuted }}>
          No rules.
        </Typography>
      )}

      <SectionLabel help={HELP.provenance}>Provenance</SectionLabel>
      <Box sx={grid}>
        <Field label="Source" help={HELP.source}>
          {provenance?.source ? <Help title={SOURCE_HELP[provenance.source]}>{provenance.source}</Help> : '—'}
        </Field>
        <Field label="Created" help={HELP.created}>
          {formatTimestamp(provenance?.created_at) || '—'}
        </Field>
        <Field label="Diff from" help={HELP.diffFrom}>
          {provenance?.diff_from ? `v${provenance.diff_from}` : '—'}
        </Field>
        {provenance?.model && (
          <Field label="Model" help={HELP.model}>
            {provenance.model}
          </Field>
        )}
        {provenance?.prompt_version && (
          <Field label="Prompt version" help={HELP.promptVersion}>
            {provenance.prompt_version}
          </Field>
        )}
        {provenance?.run_id && (
          <Field label="Run" help={HELP.run}>
            <Box component="span" sx={mono}>
              {provenance.run_id}
            </Box>
          </Field>
        )}
      </Box>
      {provenance?.validation && provenance.validation.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" sx={{ color: c.inkMuted, display: 'block' }}>
            <Help title={HELP.validation}>Validation</Help>
          </Typography>
          {provenance.validation.map((v, i) => (
            <Typography key={i} variant="body2" sx={{ mb: 0.25 }}>
              {v}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default RollbackProfilesSection;
